import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { SegmentCondition } from '@/pages/SegmentRuleFields'

export const moneyOperators = [
  ['greater_than', 'greater than'],
  ['greater_than_or_equal_to', 'at least'],
  ['equal_to', 'equal to'],
  ['less_than', 'less than'],
  ['less_than_or_equal_to', 'at most'],
] as const

interface ConditionRowProps {
  condition: SegmentCondition
  onChange: (condition: SegmentCondition) => void
  onRemove: () => void
}

export function MoneyConditionRow({ condition, onChange, onRemove }: ConditionRowProps) {
  const label = condition.field === 'open_balance' ? 'Open balance' : 'Overdue balance'
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-input px-3 py-2">
      <span className="text-body-sm">{label}</span>
      <Select
        value={condition.operator}
        onValueChange={(operator) => onChange({ ...condition, operator })}
      >
        <SelectTrigger aria-label={`${label} comparison`} className="h-8 w-40">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {moneyOperators.map(([value, text]) => (
            <SelectItem key={value} value={value}>
              {text}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Input
        aria-label={`${label} amount`}
        inputMode="decimal"
        className="h-8 w-28"
        value={String(condition.values[0] ?? '')}
        onChange={(event) => onChange({ ...condition, values: [event.target.value] })}
      />
      <RemoveButton onClick={onRemove} />
    </div>
  )
}

function integerValue(value: string) {
  return /^\d+$/.test(value) ? Number(value) : value
}

export function RenewalDueConditionRow({ condition, onChange, onRemove }: ConditionRowProps) {
  const relative = condition.operator === 'in_next_days'
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-input px-3 py-2">
      <span className="text-body-sm">Renewal-due</span>
      <Select
        value={relative ? 'relative' : 'absolute'}
        onValueChange={(value) =>
          onChange({
            field: 'renewal_due',
            operator: value === 'relative' ? 'in_next_days' : 'between',
            values: value === 'relative' ? [30] : ['', ''],
          })
        }
      >
        <SelectTrigger aria-label="Renewal-due window type" className="h-8 w-40">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="relative">in the next</SelectItem>
          <SelectItem value="absolute">between dates</SelectItem>
        </SelectContent>
      </Select>
      {relative ? (
        <>
          <Input
            aria-label="Renewal-due days"
            inputMode="numeric"
            className="h-8 w-20"
            value={String(condition.values[0] ?? '')}
            onChange={(event) => onChange({ ...condition, values: [event.target.value] })}
          />
          <span className="text-body-sm text-muted-foreground">days</span>
        </>
      ) : (
        <DateInputs condition={condition} onChange={onChange} label="Renewal-due" />
      )}
      <RemoveButton onClick={onRemove} />
    </div>
  )
}

export function DateRangeConditionRow({ condition, onChange, onRemove }: ConditionRowProps) {
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-input px-3 py-2">
      <span className="text-body-sm">Member-since between</span>
      <DateInputs condition={condition} onChange={onChange} label="Member-since" />
      <RemoveButton onClick={onRemove} />
    </div>
  )
}

export function LapsedOnConditionRow({ condition, onChange, onRemove }: ConditionRowProps) {
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-input px-3 py-2">
      <span className="text-body-sm">Lapsed in the last</span>
      <Input
        aria-label="Lapsed-on days"
        inputMode="numeric"
        className="h-8 w-20"
        value={String(condition.values[0] ?? '')}
        onChange={(event) => onChange({ ...condition, values: [integerValue(event.target.value)] })}
      />
      <span className="text-body-sm text-muted-foreground">days</span>
      <RemoveButton onClick={onRemove} />
    </div>
  )
}

function DateInputs({
  condition,
  onChange,
  label,
}: Omit<ConditionRowProps, 'onRemove'> & { label: string }) {
  return (
    <>
      <Input
        type="date"
        aria-label={`${label} from`}
        className="h-8 w-36"
        value={String(condition.values[0] ?? '')}
        onChange={(event) =>
          onChange({ ...condition, values: [event.target.value, condition.values[1] ?? ''] })
        }
      />
      <span className="text-body-sm text-muted-foreground">and</span>
      <Input
        type="date"
        aria-label={`${label} to`}
        className="h-8 w-36"
        value={String(condition.values[1] ?? '')}
        onChange={(event) =>
          onChange({ ...condition, values: [condition.values[0] ?? '', event.target.value] })
        }
      />
    </>
  )
}

function RemoveButton({ onClick }: { onClick: () => void }) {
  return (
    <Button type="button" variant="outline" onClick={onClick}>
      Remove
    </Button>
  )
}
